'use client'

import React, { FC, useMemo } from 'react'
import { Zap } from 'lucide-react'
import { cn } from '@/lib/utils'
import DragScrollRow from '@/components/drag-scroll-row'
import {
	BITMAP_EPOCH_TRAIT,
	BITMAP_EPOCH_VALUES,
	BITMAP_LEGENDARY_MEMBERS,
	BITMAP_LEGENDARY_TRAIT,
	BITMAP_QUICK_FILTERS,
	isBitmapQuickFilterActive,
	type BitmapQuickFilter
} from '@/utils/bitmap-filters'

type Filter = { trait_type: string; values: string[] }

const CHIP =
	'inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 font-mono text-[10px] font-semibold transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ring'
const CHIP_IDLE =
	'border-border bg-muted/40 text-muted-foreground hover:border-foreground/30 hover:text-foreground'
const CHIP_ON = 'border-[#9E77ED]/60 bg-[#9E77ED]/10 text-[#6941C6] dark:text-[#B692F6]'

const groupOrder: BitmapQuickFilter['group'][] = ['historical', 'punk', 'numeric', 'rarity']

/**
 * The bitmap explorer's quick filters, modelled on bitmap.trade's explorer:
 * a row of halving epochs plus a row of named trait chips. Every chip writes
 * to the same filter state the full trait sheet uses, so the two stay in sync.
 */
const BitmapExplorerBar: FC<{
	filters: Filter[]
	onChange: (trait_type: string, values: string[]) => void
	onClear?: () => void
	/** Attribute groups from the API, used to hide chips with no data. */
	attributes?: { trait_type: string; values?: { value: string; count?: number }[] }[]
	className?: string
	compact?: boolean
}> = ({ filters, onChange, onClear, attributes, className, compact }) => {
	const epochValues = filters.find((f) => f.trait_type === BITMAP_EPOCH_TRAIT)?.values || []
	const activeCount = filters.reduce((n, f) => n + (f.values?.length || 0), 0)

	const available = useMemo(() => {
		if (!attributes?.length) return BITMAP_QUICK_FILTERS
		const have = new Map<string, Set<string>>()
		for (const a of attributes) {
			have.set(a.trait_type, new Set((a.values || []).map((v) => v.value)))
		}
		return BITMAP_QUICK_FILTERS.filter((q) => {
			// Legendary is a union of other traits; show it when any member exists.
			const members =
				q.members.length === 1 && q.members[0].trait_type === BITMAP_LEGENDARY_TRAIT
					? BITMAP_LEGENDARY_MEMBERS
					: q.members
			return members.some(
				(m) => m.trait_type === BITMAP_EPOCH_TRAIT || have.get(m.trait_type)?.has(m.value)
			)
		})
	}, [attributes])

	const toggleEpoch = (value: string) => {
		onChange(BITMAP_EPOCH_TRAIT, epochValues.includes(value) ? [] : [value])
	}

	const toggleQuick = (q: BitmapQuickFilter) => {
		const on = isBitmapQuickFilterActive(q, filters)
		for (const [trait, values] of Object.entries(q.byTrait)) {
			if (on) {
				onChange(trait, [])
			} else {
				onChange(trait, values)
			}
		}
	}

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			<DragScrollRow label="Bitmap epochs" className="pb-0.5">
				{BITMAP_EPOCH_VALUES.map((e) => {
					const on = epochValues.includes(e.value)
					return (
						<button
							key={e.value}
							type="button"
							onClick={() => toggleEpoch(e.value)}
							aria-pressed={on}
							className={cn(CHIP, on ? CHIP_ON : CHIP_IDLE)}
							title={`Blocks ${e.min.toLocaleString('en-US')} to ${e.max.toLocaleString('en-US')}`}
						>
							{e.value}
						</button>
					)
				})}
			</DragScrollRow>
			<DragScrollRow label="Bitmap traits" className="pb-0.5">
				{!compact && (
					<span className="mr-1 shrink-0 font-mono text-[10px] text-muted-foreground/70">
						Traits
					</span>
				)}
				{groupOrder.map((group, gi) => {
					const chips = available.filter((q) => q.group === group)
					if (!chips.length) return null
					return (
						<React.Fragment key={group}>
							{gi > 0 && <span className="mx-0.5 h-4 w-px shrink-0 bg-border" aria-hidden />}
							{chips.map((q) => {
								const on = isBitmapQuickFilterActive(q, filters)
								return (
									<button
										key={q.id}
										type="button"
										onClick={() => toggleQuick(q)}
										aria-pressed={on}
										title={q.hint}
										className={cn(CHIP, on ? CHIP_ON : CHIP_IDLE)}
									>
										{q.id === 'legendary' && <Zap className="size-3" fill="currentColor" />}
										{q.label}
									</button>
								)
							})}
						</React.Fragment>
					)
				})}
				{activeCount > 0 && onClear && (
					<button
						type="button"
						onClick={onClear}
						className={cn(CHIP, 'border-transparent text-muted-foreground hover:text-foreground')}
					>
						Clear ({activeCount})
					</button>
				)}
			</DragScrollRow>
		</div>
	)
}

export default BitmapExplorerBar
